import React, { useEffect } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';

const DeleteAccountModal = ({ show, onClose, onConfirm, isDeleting }) => {
    useEffect(() => {
        if (!show) return;
        
        const handleEscape = (e) => {
            if (e.key === 'Escape' && !isDeleting) { // Don't allow closing while deleting
                onClose();
            }
        };
        
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [show, onClose, isDeleting]);

    if (!show) return null;

    return (
        <div 
            className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={isDeleting ? undefined : onClose} // Disable backdrop click while deleting
        >
            <div 
                className="bg-white dark:bg-slate-800 border-slate-200 dark:border-white/10 rounded-3xl border shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                <h4 className="text-xl font-bold mb-4 text-slate-900 dark:text-slate-100">Are you absolutely sure?</h4>
                <p className="text-slate-500 dark:text-slate-400 mb-8 leading-relaxed">
                    This action <span className="text-rose-500 font-semibold tracking-wide uppercase text-xs">cannot be undone</span>. All your data will be permanently wiped from our systems.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                    <button
                        onClick={onClose}
                        disabled={isDeleting}
                        className="flex-1 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-900 dark:text-slate-100 font-semibold px-6 py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isDeleting}
                        className="flex-1 bg-rose-600 hover:bg-rose-500 text-white font-semibold px-6 py-3 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isDeleting ? (
                            <>
                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                    <circle 
                                        className="opacity-25" 
                                        cx="12" 
                                        cy="12" 
                                        r="10" 
                                        stroke="currentColor" 
                                        strokeWidth="4" 
                                        fill="none" 
                                    />
                                    <path 
                                        className="opacity-75" 
                                        fill="currentColor" 
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" 
                                    />
                                </svg>
                                Deleting...
                            </>
                        ) : (
                            <>
                                <TrashIcon className="size-5" />
                                Yes, Delete
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeleteAccountModal;